import {useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import type {ActionResult} from '../actionResult';
import type {ContentPart} from '../store/thread';
import {
  type SendMessageOptions,
  type WBAgentController,
  type WBAgentControllerStore,
  wbAgentControllerStore,
} from './store';

/**
 * WBAgentControllerApi is the app-facing controller returned by
 * `useWBAgentController`. It adds `isAvailable` and widens `sendMessage` with
 * an `unavailable` outcome for when no chat is mounted to receive the message.
 */
export type WBAgentControllerApi = Omit<WBAgentController, 'sendMessage'> & {
  isAvailable: boolean;
  sendMessage(
    prompt: string | ContentPart[],
    attachments?: ContentPart[],
    opts?: SendMessageOptions
  ): ActionResult<'readonly' | 'empty' | 'unavailable'>;
};

/**
 * useWBAgentController exposes the agent chat, whether it's available and
 * various interactions, to any component in the app. `store` defaults to the
 * shared app-wide instance; tests pass their own for isolation.
 */
export function useWBAgentController(
  store: WBAgentControllerStore = wbAgentControllerStore
): WBAgentControllerApi {
  // NOTE: isAvailable trails the live runtime by one commit. Fine for buttons,
  // but never gate a useAui-rendering component on it; those belong inside
  // AgentChatRuntimeBoundary.
  const {isAvailable} = useSyncExternalStore(
    store.subscribe,
    store.getAvailability
  );

  const actions = useMemo(
    () => ({
      openChat: () => store.getHandle()?.openChat(),
      openChatToThread(rootTurnId: string) {
        store.getHandle()?.openChatToThread(rootTurnId);
      },
      sendMessage: (
        prompt: string | ContentPart[],
        attachments?: ContentPart[],
        opts?: SendMessageOptions
      ): ActionResult<'readonly' | 'empty' | 'unavailable'> =>
        store.getHandle()?.sendMessage(prompt, attachments, opts) ?? {
          ok: false,
          reason: 'unavailable',
        },
    }),
    [store]
  );

  return {isAvailable, ...actions};
}
